/**
 * Boomfection Landing — Shared Utilities
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */

import { t } from '/js/i18n.js';

/**
 * Safe JSON fetch — returns parsed JSON or null on error
 * @param {string} url
 * @returns {Promise<Object|null>}
 */
export async function safeJsonFetch(url) {
    try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const ct = res.headers.get('content-type') || '';
        if (!ct.includes('application/json')) return null;
        return await res.json();
    } catch {
        /* expected — network error or non-JSON response; caller handles null */
        return null;
    }
}

/**
 * Translates error codes to user-friendly messages
 * @param {string} error
 * @returns {string}
 */
export function getErrorMessage(error) {
    const key = `errors.${error}`;
    const translated = t(key);
    if (translated !== key) return translated;
    const fallbacks = {
        game_not_found: t('errors.game_not_found'),
        game_full: t('errors.game_full'),
        game_started: t('errors.game_started'),
        game_already_started: t('errors.game_already_started'),
        invalid_password: t('errors.invalid_password'),
        wrong_password: t('errors.wrong_password'),
        color_taken: t('errors.color_taken'),
        not_your_turn: t('errors.not_your_turn'),
        invalid_move: t('errors.invalid_move'),
        field_not_clickable: t('errors.field_not_clickable'),
        game_not_playing: t('errors.game_not_playing'),
        not_host: t('errors.not_host'),
        not_enough_players: t('errors.not_enough_players'),
        guests_disabled: t('errors.guests_disabled'),
        guest_no_speed: t('errors.guest_no_speed'),
        guest_no_team: t('errors.guest_no_team'),
        guest_no_create: t('errors.guest_no_create'),
        guest_daily_limit: t('errors.guest_daily_limit'),
        guest_limit_reached: t('errors.guest_limit_reached'),
        not_in_game: t('errors.not_in_game'),
        create_failed: t('errors.create_failed'),
        join_failed: t('errors.join_failed'),
        move_failed: t('errors.move_failed'),
        ai_add_failed: t('errors.ai_add_failed'),
        ai_remove_failed: t('errors.ai_remove_failed'),
        banned: t('errors.banned'),
        rate_limited: t('errors.rate_limited')
    };
    return fallbacks[error] || error;
}

/**
 * Shows a toast notification with icon, progress bar, and slide animation
 * @param {string} text
 * @param {string} type - 'info' | 'success' | 'error' | 'warning'
 */
export function showNotification(text, type = 'info') {
    const container = document.getElementById('toast-container');
    if (!container) {
        console.warn(`[${type.toUpperCase()}]`, text);
        return;
    }

    const toast = document.createElement('div');
    const duration = type === 'error' ? 8000 : type === 'warning' ? 6000 : 4000;
    toast.className = `toast toast-${type} animate-slide-in`;
    toast.style.setProperty('--toast-duration', `${duration}ms`);

    const iconMap = {
        success: '\u2713',
        error: '\u2717',
        info: '\u2139',
        warning: '\u26A0'
    };
    const icon = iconMap[type] || iconMap.info;

    const iconSpan = document.createElement('span');
    iconSpan.className = 'toast-icon';
    iconSpan.textContent = icon;
    iconSpan.setAttribute('aria-hidden', 'true');

    const textSpan = document.createElement('span');
    textSpan.className = 'toast-text';
    textSpan.textContent = text;

    const closeSpan = document.createElement('span');
    closeSpan.className = 'toast-close';
    closeSpan.textContent = '\u00D7';
    closeSpan.setAttribute('aria-label', 'Close');

    // Progress bar
    const progress = document.createElement('div');
    progress.className = 'toast-progress';

    toast.appendChild(iconSpan);
    toast.appendChild(textSpan);
    toast.appendChild(closeSpan);
    toast.appendChild(progress);

    closeSpan.addEventListener('click', (e) => {
        e.stopPropagation();
        toast.classList.add('toast-fade-out');
        setTimeout(() => toast.remove(), 300);
    });

    container.appendChild(toast);

    setTimeout(() => {
        if (toast.parentNode) {
            toast.classList.add('toast-fade-out');
            setTimeout(() => toast.remove(), 300);
        }
    }, duration);
}
